import { supabase } from '@/integrations/supabase/client';

export interface PhotoAnalysisResult {
  suggestedPropertyType: string;
  suggestedCategory: 'residential' | 'commercial' | 'land';
  suggestedCondition: string;
  visibleFeatures: string[];
  roomsIdentified: string[];
  qualityTier: 'luxury' | 'high-end' | 'standard' | 'budget' | 'needs-work';
  priceRangeSuggestion: {
    min: number;
    max: number;
    currency: string;
    basis: string;
  } | null;
  confidence: number;
  notes: string[];
}

/**
 * Analyzes property photos to auto-detect:
 * - Property type (apartment, house, villa, etc.)
 * - Property condition (new, renovated, good, needs-renovation)
 * - Visible features (pool, balcony, sea-view, etc.)
 * - Room types visible
 * - Quality tier
 * - Price range suggestion
 */
export async function analyzePhotos(imageBase64Array: string[]): Promise<PhotoAnalysisResult> {
  if (!imageBase64Array || imageBase64Array.length === 0) {
    throw new Error('At least one image is required for analysis');
  }

  const { data, error } = await supabase.functions.invoke('analyze-photos', {
    body: { images: imageBase64Array }
  });

  if (error) {
    console.error('Photo analysis error:', error);
    throw new Error(error.message || 'Failed to analyze photos');
  }

  return data as PhotoAnalysisResult;
}

/**
 * Maps quality tier to a price multiplier for estimation
 */
export function getQualityMultiplier(qualityTier: string): number {
  switch (qualityTier) {
    case 'luxury': return 1.5;
    case 'high-end': return 1.2;
    case 'standard': return 1.0;
    case 'budget': return 0.8;
    case 'needs-work': return 0.6;
    default: return 1.0;
  }
}

/**
 * Maps feature IDs from analysis to our internal feature IDs
 */
export function normalizeAnalysisFeatures(features: string[]): string[] {
  const featureMap: Record<string, string> = {
    'pool': 'pool',
    'swimming pool': 'pool',
    'communal-pool': 'communal-pool',
    'communal pool': 'communal-pool',
    'garden': 'garden',
    'balcony': 'balcony',
    'terrace': 'terrace',
    'covered-parking': 'covered-parking',
    'covered parking': 'covered-parking',
    'parking': 'parking',
    'garage': 'garage',
    'sea-view': 'sea-view',
    'sea view': 'sea-view',
    'ocean view': 'sea-view',
    'mountain-view': 'mountain-view',
    'mountain view': 'mountain-view',
    'city-view': 'city-view',
    'city view': 'city-view',
    'ac': 'ac',
    'air conditioning': 'ac',
    'air-conditioning': 'ac',
    'fireplace': 'fireplace',
    'jacuzzi': 'jacuzzi',
    'sauna': 'sauna',
    'central-heating': 'central-heating',
    'central heating': 'central-heating',
    'elevator': 'elevator',
    'lift': 'elevator',
    'security': 'security',
    'security system': 'security',
    'storage-room': 'storage-room',
    'storage room': 'storage-room',
    'storage': 'storage-room',
    'sea-access': 'sea-access',
    'beach access': 'sea-access',
    'road-access': 'road-access',
    'bbq': 'bbq',
    'barbecue': 'bbq',
    'gym': 'gym-fitness',
    'gym-fitness': 'gym-fitness',
    'fitness': 'gym-fitness',
  };

  const normalized: string[] = [];
  for (const feature of features) {
    const lower = feature.toLowerCase().trim();
    if (featureMap[lower]) {
      if (!normalized.includes(featureMap[lower])) {
        normalized.push(featureMap[lower]);
      }
    } else {
      // Keep unrecognized features as-is (they might be valid)
      if (!normalized.includes(lower)) {
        normalized.push(lower);
      }
    }
  }
  
  return normalized;
}
